import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Line, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import "./Spinner.css";

ChartJS.register(
    CategoryScale, LinearScale, LineElement, PointElement,
    BarElement, Title, Tooltip, Legend, Filler
);

const getBackendUrl = () => {
    const backendHost = window.location.hostname;
    const isSecure = window.location.protocol === "https:";
    return isSecure ? `https://${backendHost}` : `http://${backendHost}:5001`;
};

// --- DepthCharts sub-component: 4 charts in 2x2 grid ---
const DepthCharts = ({ data, tradingsymbol }) => {
    const [selectedIdx, setSelectedIdx] = useState(0);
    const chartRef1 = useRef(null);
    const chartRef2 = useRef(null);

    const handleChartClick = useCallback((event, elements) => {
        if (elements && elements.length > 0) {
            setSelectedIdx(elements[0].index);
        }
    }, []);

    if (!data || data.length === 0) return null;

    const labels = data.map((r) => {
        if (!r.timestamp) return "";
        const t = new Date(r.timestamp);
        return t.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
    });

    const chartClickOpts = { onClick: handleChartClick };

    // Chart #1: Price — LTP + Best Bid/Ask
    const priceData = {
        labels,
        datasets: [
            {
                label: "LTP",
                data: data.map((r) => r.ltp),
                borderColor: "#2196F3",
                backgroundColor: "rgba(33,150,243,0.1)",
                borderWidth: 1.5,
                pointRadius: 0,
                tension: 0.2,
            },
            {
                label: "Best Bid",
                data: data.map((r) => r.bid_price_1),
                borderColor: "#4CAF50",
                backgroundColor: "rgba(76,175,80,0.05)",
                borderWidth: 1,
                pointRadius: 0,
                tension: 0.2,
                fill: false,
            },
            {
                label: "Best Ask",
                data: data.map((r) => r.ask_price_1),
                borderColor: "#F44336",
                backgroundColor: "rgba(244,67,54,0.05)",
                borderWidth: 1,
                pointRadius: 0,
                tension: 0.2,
                fill: "-1", // fill between bid and ask
            },
        ],
    };

    const priceOptions = {
        ...chartClickOpts,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: `#1 Price: LTP + Best Bid/Ask — ${tradingsymbol}`, font: { size: 14 } },
            legend: { position: "top", labels: { usePointStyle: true, boxWidth: 8 } },
            tooltip: { mode: "index", intersect: false },
        },
        scales: {
            x: { ticks: { maxTicksLimit: 15, maxRotation: 45, font: { size: 10 } } },
            y: { title: { display: true, text: "Price (₹)" } },
        },
        interaction: { mode: "nearest", axis: "x", intersect: false },
    };

    // Chart #2: Bid-Ask Spread
    const spreadData = {
        labels,
        datasets: [
            {
                label: "Spread (₹)",
                data: data.map((r) => r.spread),
                borderColor: "#FF9800",
                backgroundColor: "rgba(255,152,0,0.15)",
                borderWidth: 1.5,
                pointRadius: 0,
                fill: true,
                tension: 0.2,
            },
        ],
    };

    const spreadOptions = {
        ...chartClickOpts,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: "#2 Bid-Ask Spread (₹)", font: { size: 14 } },
            legend: { display: false },
            tooltip: { mode: "index", intersect: false },
        },
        scales: {
            x: { ticks: { maxTicksLimit: 15, maxRotation: 45, font: { size: 10 } } },
            y: { title: { display: true, text: "Spread (₹)" }, beginAtZero: true },
        },
        interaction: { mode: "nearest", axis: "x", intersect: false },
    };

    // Chart #3: Order Book Snapshot at selected time
    const selectedRow = data[selectedIdx] || data[0];
    const bidLevels = [];
    const askLevels = [];
    for (let i = 1; i <= 5; i++) {
        bidLevels.push({ price: selectedRow[`bid_price_${i}`], qty: selectedRow[`bid_qty_${i}`] });
        askLevels.push({ price: selectedRow[`ask_price_${i}`], qty: selectedRow[`ask_qty_${i}`] });
    }

    const obLabels = [
        ...bidLevels.map((b, i) => `Bid ${i + 1}: ₹${(b.price || 0).toFixed(2)}`).reverse(),
        ...askLevels.map((a, i) => `Ask ${i + 1}: ₹${(a.price || 0).toFixed(2)}`),
    ];
    const obData = [
        ...bidLevels.map((b) => b.qty || 0).reverse(),
        ...askLevels.map((a) => a.qty || 0),
    ];
    const obColors = [
        ...Array(5).fill("rgba(76,175,80,0.7)"),
        ...Array(5).fill("rgba(244,67,54,0.7)"),
    ];

    const selectedTime = selectedRow.timestamp
        ? new Date(selectedRow.timestamp).toLocaleTimeString("en-IN", {
              hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
          })
        : "";

    const orderBookData = {
        labels: obLabels,
        datasets: [
            {
                label: "Quantity",
                data: obData,
                backgroundColor: obColors,
                borderWidth: 0,
            },
        ],
    };

    const orderBookOptions = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: `#3 Order Book @ ${selectedTime}`, font: { size: 14 } },
            legend: { display: false },
        },
        scales: {
            x: { title: { display: true, text: "Quantity" }, beginAtZero: true },
        },
    };

    // Chart #4: Volume
    const volumeData = {
        labels,
        datasets: [
            {
                label: "Cumulative Volume",
                data: data.map((r) => r.volume),
                backgroundColor: "rgba(158,158,158,0.5)",
                borderColor: "rgba(117,117,117,0.8)",
                borderWidth: 1,
            },
        ],
    };

    const volumeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: "#4 Volume", font: { size: 14 } },
            legend: { display: false },
        },
        scales: {
            x: { ticks: { maxTicksLimit: 15, maxRotation: 45, font: { size: 10 } } },
            y: { title: { display: true, text: "Volume" }, beginAtZero: true },
        },
    };

    const gridStyle = {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
        marginTop: "16px",
    };

    const chartBox = {
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        padding: "12px",
        height: "340px",
    };

    return (
        <div>
            <div style={gridStyle}>
                <div style={chartBox}>
                    <Line ref={chartRef1} data={priceData} options={priceOptions} />
                </div>
                <div style={chartBox}>
                    <Line ref={chartRef2} data={spreadData} options={spreadOptions} />
                </div>
                <div style={chartBox}>
                    <Bar data={orderBookData} options={orderBookOptions} />
                    <div style={{ textAlign: "center", fontSize: "11px", color: "#999", marginTop: "4px" }}>
                        Click Chart #1 or #2 to update
                    </div>
                </div>
                <div style={chartBox}>
                    <Bar data={volumeData} options={volumeOptions} />
                </div>
            </div>
        </div>
    );
};


const MarketData = () => {
    const [dates, setDates] = useState({ depth: [], snapshots: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Detail panels
    const [depthDetail, setDepthDetail] = useState(null);
    const [depthDetailLoading, setDepthDetailLoading] = useState(false);
    const [selectedDepthDate, setSelectedDepthDate] = useState(null);

    const [snapDetail, setSnapDetail] = useState(null);
    const [snapDetailLoading, setSnapDetailLoading] = useState(false);
    const [selectedSnapDate, setSelectedSnapDate] = useState(null);

    // Company depth charts state
    const [instruments, setInstruments] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [companyData, setCompanyData] = useState(null);
    const [companyDataLoading, setCompanyDataLoading] = useState(false);
    const [selectedDepthItem, setSelectedDepthItem] = useState(null);

    useEffect(() => {
        fetchDates();
    }, []);

    const fetchDates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${getBackendUrl()}/api/marketdata/dates`);
            setDates(response.data);
        } catch (err) {
            setError(err.message || "Failed to fetch dates");
        } finally {
            setLoading(false);
        }
    };

    const fetchDepthDetail = async (item) => {
        if (selectedDepthDate === item.date) {
            setSelectedDepthDate(null);
            setDepthDetail(null);
            setInstruments([]);
            setSelectedCompany("");
            setCompanyData(null);
            setSelectedDepthItem(null);
            return;
        }
        setSelectedDepthDate(item.date);
        setSelectedDepthItem(item);
        setDepthDetailLoading(true);
        setDepthDetail(null);
        setInstruments([]);
        setSelectedCompany("");
        setCompanyData(null);

        try {
            // Load detail and instruments in parallel
            const [detailRes, instrRes] = await Promise.all([
                axios.post(`${getBackendUrl()}/api/marketdata/depth_detail`, {
                    key: item.key, date: item.date, max_rows: 50,
                }),
                axios.post(`${getBackendUrl()}/api/marketdata/depth_instruments`, {
                    key: item.key, date: item.date,
                }).catch(() => null),
            ]);
            setDepthDetail(detailRes.data);
            if (instrRes && instrRes.data && instrRes.data.instruments) {
                setInstruments(instrRes.data.instruments);
            }
        } catch (err) {
            setDepthDetail({ error: err.message || "Failed to load detail" });
        } finally {
            setDepthDetailLoading(false);
        }
    };

    const fetchCompanyData = async (tradingsymbol) => {
        if (!selectedDepthItem || !tradingsymbol) return;
        setSelectedCompany(tradingsymbol);
        setCompanyDataLoading(true);
        setCompanyData(null);
        try {
            const response = await axios.post(`${getBackendUrl()}/api/marketdata/depth_company`, {
                key: selectedDepthItem.key,
                date: selectedDepthItem.date,
                tradingsymbol: tradingsymbol,
                sample_interval: 10,
            });
            setCompanyData(response.data);
        } catch (err) {
            setCompanyData({ error: err.message || "Failed to load company data" });
        } finally {
            setCompanyDataLoading(false);
        }
    };

    const fetchSnapDetail = async (item) => {
        if (selectedSnapDate === item.date) {
            setSelectedSnapDate(null);
            setSnapDetail(null);
            return;
        }
        setSelectedSnapDate(item.date);
        setSnapDetailLoading(true);
        setSnapDetail(null);
        try {
            const response = await axios.post(`${getBackendUrl()}/api/marketdata/snapshot_detail`, {
                key: item.key,
                max_rows: 50,
            });
            setSnapDetail(response.data);
        } catch (err) {
            setSnapDetail({ error: err.message || "Failed to load detail" });
        } finally {
            setSnapDetailLoading(false);
        }
    };

    const styles = {
        container: {
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "20px",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
        },
        title: { fontSize: "24px", fontWeight: "bold", margin: 0 },
        backBtn: {
            background: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            padding: "8px 16px",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "pointer",
            textDecoration: "none",
        },
        section: {
            marginBottom: "32px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            padding: "16px",
        },
        sectionTitle: { fontSize: "18px", fontWeight: "bold", marginBottom: "12px" },
        table: {
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
        },
        th: {
            textAlign: "left",
            padding: "8px 12px",
            borderBottom: "2px solid #dee2e6",
            backgroundColor: "#f8f9fa",
            fontWeight: "600",
        },
        td: {
            padding: "8px 12px",
            borderBottom: "1px solid #eee",
        },
        clickableRow: {
            cursor: "pointer",
        },
        selectedRow: {
            backgroundColor: "#e8f4fd",
        },
        detailPanel: {
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderRadius: "6px",
            marginTop: "12px",
        },
        statGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            marginBottom: "16px",
        },
        statCard: {
            backgroundColor: "#fff",
            padding: "12px",
            borderRadius: "6px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        },
        statLabel: { fontSize: "12px", color: "#666", marginBottom: "4px" },
        statValue: { fontSize: "18px", fontWeight: "bold", color: "#333" },
        sampleTableWrap: {
            overflowX: "auto",
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
        },
        sampleTh: {
            textAlign: "left",
            padding: "6px 10px",
            borderBottom: "2px solid #dee2e6",
            backgroundColor: "#e9ecef",
            fontSize: "12px",
            fontWeight: "600",
            whiteSpace: "nowrap",
            position: "sticky",
            top: 0,
        },
        sampleTd: {
            padding: "4px 10px",
            borderBottom: "1px solid #f0f0f0",
            fontSize: "12px",
            whiteSpace: "nowrap",
        },
        errorText: { color: "#dc3545", padding: "12px" },
    };

    const renderDetailPanel = (detail, isLoading) => {
        if (isLoading) {
            return (
                <div style={styles.detailPanel}>
                    <div className="spinner" style={{ margin: "20px auto" }}></div>
                </div>
            );
        }
        if (!detail) return null;
        if (detail.error) {
            return (
                <div style={styles.detailPanel}>
                    <p style={styles.errorText}>{detail.error}</p>
                </div>
            );
        }

        return (
            <div style={styles.detailPanel}>
                <div style={styles.statGrid}>
                    <div style={styles.statCard}>
                        <div style={styles.statLabel}>Total Rows</div>
                        <div style={styles.statValue}>{(detail.num_rows || 0).toLocaleString()}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statLabel}>Columns</div>
                        <div style={styles.statValue}>{detail.num_columns}</div>
                    </div>
                    {detail.num_row_groups !== undefined && (
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Row Groups</div>
                            <div style={styles.statValue}>{detail.num_row_groups}</div>
                        </div>
                    )}
                </div>
                <div style={{ marginBottom: "8px", fontSize: "13px", color: "#555" }}>
                    <strong>Columns:</strong> {(detail.columns || []).join(", ")}
                </div>
                {detail.sample_rows && detail.sample_rows.length > 0 && (
                    <>
                        <div style={{ fontSize: "13px", fontWeight: "bold", marginBottom: "6px" }}>
                            Sample Data ({detail.sample_rows.length} rows)
                        </div>
                        <div style={styles.sampleTableWrap}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        {Object.keys(detail.sample_rows[0]).map((col) => (
                                            <th key={col} style={styles.sampleTh}>{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.sample_rows.map((row, idx) => (
                                        <tr key={idx}>
                                            {Object.values(row).map((val, ci) => (
                                                <td key={ci} style={styles.sampleTd}>
                                                    {val === null || val === undefined ? "-" : String(val)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Market Data Explorer</h1>
                <Link to="/" style={styles.backBtn}>Back to Home</Link>
            </div>

            {loading && <div className="spinner" style={{ margin: "40px auto" }}></div>}
            {error && <p style={styles.errorText}>{error}</p>}

            {/* Section 1: Bid-Ask Depth */}
            <div style={styles.section}>
                <div style={styles.sectionTitle}>Bid-Ask Depth Files</div>
                {dates.depth.length === 0 && !loading ? (
                    <p style={{ color: "#888", fontSize: "14px" }}>No depth files found.</p>
                ) : (
                    <>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Size (MB)</th>
                                    <th style={styles.th}>Last Modified</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dates.depth.map((item) => (
                                    <tr
                                        key={item.date}
                                        style={{
                                            ...styles.clickableRow,
                                            ...(selectedDepthDate === item.date ? styles.selectedRow : {}),
                                        }}
                                        onClick={() => fetchDepthDetail(item)}
                                    >
                                        <td style={styles.td}>{item.date}</td>
                                        <td style={styles.td}>{item.size_mb}</td>
                                        <td style={styles.td}>
                                            {new Date(item.last_modified).toLocaleString("en-IN", {
                                                timeZone: "Asia/Kolkata",
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {selectedDepthDate && renderDetailPanel(depthDetail, depthDetailLoading)}

                        {/* Company selector + depth charts */}
                        {selectedDepthDate && !depthDetailLoading && depthDetail && !depthDetail.error && (
                            <div style={{ marginTop: "16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                    <label style={{ fontWeight: "bold", fontSize: "14px" }}>Company:</label>
                                    {instruments.length > 0 ? (
                                        <select
                                            value={selectedCompany}
                                            onChange={(e) => fetchCompanyData(e.target.value)}
                                            style={{
                                                padding: "6px 12px", fontSize: "14px", borderRadius: "4px",
                                                border: "1px solid #ccc", minWidth: "250px",
                                            }}
                                        >
                                            <option value="">-- Select a company ({instruments.length} available) --</option>
                                            {instruments.map((sym) => (
                                                <option key={sym} value={sym}>{sym}</option>
                                            ))}
                                        </select>
                                    ) : depthDetailLoading ? (
                                        <span style={{ fontSize: "13px", color: "#888" }}>
                                            Loading companies... (first load may take 30-60s)
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: "13px", color: "#888" }}>
                                            No instruments found
                                        </span>
                                    )}
                                </div>

                                {companyDataLoading && (
                                    <div className="spinner" style={{ margin: "20px auto" }}></div>
                                )}

                                {companyData && companyData.error && (
                                    <p style={styles.errorText}>{companyData.error}</p>
                                )}

                                {companyData && companyData.data && (
                                    <div>
                                        <div style={{ fontSize: "13px", color: "#555", marginBottom: "8px" }}>
                                            Showing {companyData.rows} data points
                                            (sampled every {companyData.sample_interval}s
                                            from {companyData.total_rows_before_sample.toLocaleString()} total rows)
                                        </div>
                                        <DepthCharts data={companyData.data} tradingsymbol={selectedCompany} />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Section 2: Quote Snapshots */}
            <div style={styles.section}>
                <div style={styles.sectionTitle}>Quote Snapshots</div>
                {dates.snapshots.length === 0 && !loading ? (
                    <p style={{ color: "#888", fontSize: "14px" }}>No snapshot files found.</p>
                ) : (
                    <>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Size (MB)</th>
                                    <th style={styles.th}>Last Modified</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dates.snapshots.map((item) => (
                                    <tr
                                        key={item.date}
                                        style={{
                                            ...styles.clickableRow,
                                            ...(selectedSnapDate === item.date ? styles.selectedRow : {}),
                                        }}
                                        onClick={() => fetchSnapDetail(item)}
                                    >
                                        <td style={styles.td}>{item.date}</td>
                                        <td style={styles.td}>{item.size_mb}</td>
                                        <td style={styles.td}>
                                            {new Date(item.last_modified).toLocaleString("en-IN", {
                                                timeZone: "Asia/Kolkata",
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {selectedSnapDate && renderDetailPanel(snapDetail, snapDetailLoading)}
                    </>
                )}
            </div>
        </div>
    );
};

export default MarketData;
